import React from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import countryData from "country-telephone-data";
import { useGetCountries } from "../api/countrycode";

const fallbackCountries = countryData.allCountries
  .map((country) => ({
    name: country.name,
    code: country.iso2?.toUpperCase(),
    callingCode: `+${country.dialCode}`,
    flag: country.iso2 ? `https://flagcdn.com/24x18/${country.iso2}.png` : "",
  }))
  .filter((country) => country.callingCode)
  .sort((a, b) => a.name.localeCompare(b.name));

const getMenuWidth = (size) => {
  if (size === "inline") return 260;
  if (size === "compact") return 280;
  return 320;
};

const CountryCodePicker = ({
  value,
  countryCode,
  onChange,
  disabled = false,
  size = "compact",
  error = false,
  name,
  onBlur,
  fullWidth = true,
  sx,
}) => {
  const { countries, isLoading } = useGetCountries();
  const options = countries.length ? countries : fallbackCountries;
  const selectedValue = value || countryCode || "+91";
  const selectedCountry =
    options.find((country) => country.callingCode === selectedValue) ||
    fallbackCountries.find((country) => country.callingCode === selectedValue);

  const handleChange = (event) => {
    const nextValue = event.target.value;
    const option = options.find((country) => country.callingCode === nextValue);
    onChange?.(nextValue, option);
  };

  const compact = size === "compact";
  const inline = size === "inline";

  return (
    <FormControl
      fullWidth={fullWidth}
      error={error}
      disabled={disabled}
      sx={{
        minWidth: inline ? 88 : "auto",
        ...sx,
      }}
    >
      <Select
        name={name}
        value={selectedValue}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        displayEmpty
        variant={inline ? "standard" : "outlined"}
        disableUnderline={inline}
        IconComponent={() => null}
        MenuProps={{
          PaperProps: {
            sx: {
              width: getMenuWidth(size),
              maxHeight: 320,
              mt: 0.5,
              borderRadius: "8px",
              border: "1px solid var(--app-border)",
              backgroundColor: "var(--app-surface)",
              boxShadow: "var(--app-shadow)",
            },
          },
        }}
        renderValue={(selected) => {
          const country =
            options.find((item) => item.callingCode === selected) ||
            selectedCountry;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {country?.flag && (
                <Avatar
                  src={country.flag}
                  alt={country.code}
                  variant="square"
                  sx={{
                    width: inline ? 22 : 20,
                    height: inline ? 16 : 14,
                    borderRadius: "2px",
                    flex: "0 0 auto",
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: inline ? 16 : 14,
                  fontWeight: inline ? 600 : 400,
                  color: disabled
                    ? "var(--app-text-muted)"
                    : "var(--app-text)",
                  lineHeight: 1,
                }}
              >
                {selected || "+91"}
              </Typography>
              {isLoading && !countries.length && !inline && (
                <CircularProgress size={12} thickness={5} />
              )}
            </Box>
          );
        }}
        sx={{
          height: inline ? "auto" : 48,
          borderRadius: inline ? 0 : "8px",
          backgroundColor: disabled
            ? "var(--app-surface-2)"
            : "var(--app-surface)",
          cursor: disabled ? "not-allowed" : "pointer",
          fontFamily: "Figtree",
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            minHeight: "0 !important",
            py: inline ? "0 !important" : "12px !important",
            pl: inline ? "0 !important" : "8px !important",
            pr: inline ? "0 !important" : compact ? "28px !important" : "32px !important",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--app-border)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: disabled ? "var(--app-border)" : "var(--app-text-muted)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--app-primary)",
            borderWidth: "1px",
          },
          "&.Mui-disabled": {
            opacity: 1,
            cursor: "not-allowed",
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--app-border)",
          },
          "& .MuiSvgIcon-root": {
            color: "var(--app-text-muted)",
            right: 4,
          },
        }}
      >
        {options.map((country) => (
          <MenuItem
            key={`${country.code}-${country.callingCode}-${country.name}`}
            value={country.callingCode}
            sx={{
              gap: 1,
              minHeight: 42,
              color: "var(--app-text)",
              backgroundColor: "var(--app-surface)",
              "&.Mui-selected, &.Mui-selected:hover": {
                backgroundColor: "var(--app-surface-2)",
              },
              "&:hover": {
                backgroundColor: "var(--app-surface-2)",
              },
            }}
          >
            <Avatar
              src={country.flag}
              alt={country.code}
              variant="square"
              sx={{ width: 20, height: 14, borderRadius: "2px", flex: "0 0 auto" }}
            />
            <Typography variant="body2" noWrap>
              {country.name} ({country.callingCode})
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CountryCodePicker;
